// Cluster identities (docs/01-architecture.md section 12, item 1). The signed
// digest (section 6.2) and the key derivation (section 9) name the program id
// and nothing else about the cluster, so the program id is the only thing
// that keeps one cluster's keys and signatures apart from another's: each
// cluster must run its own. Devnet ran under mainnet's id from 2026-09-24
// until the redeploy in docs/13-devnet-redeploy.md (docs/03-open-questions.md,
// "Coder notes, 2026-09-28"). Pure: callers fetch the genesis hash themselves.

import { getBase58Encoder } from '@solana/kit';

/** The mainnet-beta program, deployed 2026-09-24 (docs/evidence/mainnet/2026-09-24/deploy-receipt.json). */
export const MAINNET_PROGRAM_ID = 'BsKbo3shF4X5nG3sMcBApDGcDM7ocC7Gun4ycBqsfrRo';

/** `getGenesisHash` of each public cluster, read 2026-09-28 from api.mainnet-beta.solana.com and api.devnet.solana.com. */
export const GENESIS_HASH = {
  mainnet: '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdpKuc147dw2N9d',
  devnet: 'EtWTRABZaYq6iMfeYKouRu166VU2xqa1wcaWoxPkrZBG',
} as const;

export type Cluster = keyof typeof GENESIS_HASH;

/**
 * Why `programIdB58` must not be used by tooling that expects `cluster`, on an
 * endpoint whose genesis hash is `genesisHash`; null if it may. Mainnet runs
 * only MAINNET_PROGRAM_ID, and no other cluster may run it.
 */
export function deploymentMismatch(cluster: Cluster, programIdB58: string, genesisHash: string): string | null {
  if (cluster === 'mainnet' && programIdB58 !== MAINNET_PROGRAM_ID) return `mainnet runs program ${MAINNET_PROGRAM_ID}, not ${programIdB58}`;
  if (cluster !== 'mainnet' && programIdB58 === MAINNET_PROGRAM_ID) return `${programIdB58} is the mainnet program id; ${cluster} must run its own (docs/13-devnet-redeploy.md)`;
  if (genesisHash !== GENESIS_HASH[cluster]) return `the endpoint's genesis hash ${genesisHash} is not ${cluster}'s (${GENESIS_HASH[cluster]})`;
  return null;
}

// ------------------------------------------------------------------ the approved deployments (docs/18-one-fee-reserve.md 2.5)
//
// The fee branch (derive.ts `feeSeed`) binds a program id and a 32-byte
// cluster tag. Both come from one entry of this table, never from a URL and
// never from an RPC answer: the worker takes them once through `setFeeScope`
// (worker.ts). The app will select the entry by name at build time (its
// VITE_DEPLOYMENT) and compile it into the worker bundle, and the engine's
// deployment check (`engine.assertDeployment`, a later Part A stage) will
// compare the RPC's `getGenesisHash` (relayer.ts `Rpc.getGenesisHash`) with
// the entry's tag before any funding address is shown or any fee signature is
// made; that read is only a comparison, never a derivation input.
// sdk/rust/src/clusters.rs mirrors the table for the fee vectors.

/** The devnet program, redeployed under its own id (docs/13-devnet-redeploy.md). */
export const DEVNET_PROGRAM_ID = '48DpFqZJP6tp9hszZzVyW2hwjyN7JR9y3WyptaPtVfDn';
/** What the shim's tag is the SHA-256 of; the tag is pinned as a literal and this preimage lets a test recompute it. */
export const SHIM_GENESIS_TAG_PREIMAGE = 'THEVAULT/TEST-CLUSTER/1';
/**
 * The shim's test cluster tag, SHA-256("THEVAULT/TEST-CLUSTER/1") in base58, pinned here and in sdk/rust/src/clusters.rs.
 * tools/rpc-shim will pin it too when it gains `getGenesisHash` (a later Part A stage). It is not a public cluster's
 * genesis hash.
 */
export const SHIM_GENESIS_TAG = 'HnX8aPCrs4Tea21y99oBxiGjVRViNt84wNRjY48X54AD';

export type DeploymentName = 'mainnet' | 'devnet' | 'shim';

/** One approved deployment: its program id and its cluster tag (a public cluster's genesis hash, or the shim's fixed tag), both base58. */
export interface Deployment {
  readonly name: DeploymentName;
  readonly programId: string;
  readonly genesisHash: string;
}

/**
 * One entry per approved deployment. The shim serves mainnet's program id (tools/rpc-shim); its entry pairs that id with
 * the test tag, which the shim will answer once it gains `getGenesisHash`, so end-to-end fee addresses are never
 * mainnet's for the same words.
 */
export const DEPLOYMENTS: Readonly<Record<DeploymentName, Deployment>> = {
  mainnet: { name: 'mainnet', programId: MAINNET_PROGRAM_ID, genesisHash: GENESIS_HASH.mainnet },
  devnet: { name: 'devnet', programId: DEVNET_PROGRAM_ID, genesisHash: GENESIS_HASH.devnet },
  shim: { name: 'shim', programId: MAINNET_PROGRAM_ID, genesisHash: SHIM_GENESIS_TAG },
};

/** The entry named `name`; throws for any other string, so a build that names an unknown deployment fails. */
export function deployment(name: string): Deployment {
  const entry = Object.prototype.hasOwnProperty.call(DEPLOYMENTS, name) ? DEPLOYMENTS[name as DeploymentName] : undefined;
  if (!entry) throw new Error(`unknown deployment "${name}"; the approved deployments are ${Object.keys(DEPLOYMENTS).join(', ')}`);
  return entry;
}

/** The scope the worker compiles in (docs/18 2.4): the 32 raw bytes of an entry's program id and cluster tag. */
export interface FeeScope {
  readonly programId: Uint8Array;
  readonly clusterTag: Uint8Array;
}

function base58Bytes(s: string, what: string): Uint8Array {
  const bytes = new Uint8Array(getBase58Encoder().encode(s));
  if (bytes.length !== 32) throw new Error(`${what} ${s} decodes to ${bytes.length} bytes, not 32`);
  return bytes;
}

export function feeScopeOf(d: Deployment): FeeScope {
  return { programId: base58Bytes(d.programId, 'program id'), clusterTag: base58Bytes(d.genesisHash, 'genesis hash') };
}
